import React, { useEffect, useState } from 'react';

import { Anchor, Button, Group, List, Modal, Stack, Text, Title } from '@mantine/core';
import DocumentAutoComplete, {
  DocumentAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/DocumentAutoComplete';
import KiloAlert from 'components/KiloAlert';
import useWaiverCandidates, { WaiverCandidateFilters } from 'hooks/athlete/useWaiverCandidates';
import useSendWaivers from 'hooks/document/useSendWaivers';
import { Link } from 'react-router-dom';
import { IssueBulkWaiverResult } from 'types/generated';
import notificationHandler from 'utils/notifications';

import AthleteCandidateTable from './AthleteCandidateTable';

const EMPTY_TEMPLATE: DocumentAutoCompleteValue = { documentId: '', value: '', label: '' };

const FAILURE_REASONS: Record<string, string> = {
  WAIVER_ALREADY_SIGNED: 'Already has a signed waiver',
  ATHLETE_NOT_FOUND: 'No longer in this gym',
  REQUEST_FAILED: "Couldn't confirm it was sent; check their documents before resending",
};

interface ISendWaiverModalProps {
  opened: boolean;
  onClose: () => void;
  filters: WaiverCandidateFilters;
  fullScreen?: boolean;
  showTemplatesLink?: boolean;
}

const SendWaiverModal = ({
  opened,
  onClose,
  filters,
  fullScreen = false,
  showTemplatesLink = true,
}: ISendWaiverModalProps): JSX.Element => {
  const { successNotification } = notificationHandler();

  const [step, setStep] = useState<'template' | 'review' | 'result'>('template');
  const [template, setTemplate] = useState<DocumentAutoCompleteValue>(EMPTY_TEMPLATE);
  const [selectedIds, setSelectedIds] = useState<Set<string> | null>(null);
  const [sendingTotal, setSendingTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [result, setResult] = useState<IssueBulkWaiverResult | null>(null);
  const [stoppedMessage, setStoppedMessage] = useState<string | null>(null);

  const { data: candidates, isLoading } = useWaiverCandidates(filters, { enabled: opened });
  const sendWaivers = useSendWaivers();

  const candidateList = candidates ?? [];
  const selected = selectedIds ?? new Set<string>();

  // Preselect once per opening; a background refetch must not undo the staff member's choices.
  useEffect(() => {
    if (opened && candidates && selectedIds === null) {
      setSelectedIds(new Set(candidates.filter(c => c.preselected).map(c => c.athleteId)));
    }
  }, [opened, candidates, selectedIds]);

  const handleClose = (): void => {
    setStep('template');
    setTemplate(EMPTY_TEMPLATE);
    setSelectedIds(null);
    setResult(null);
    setStoppedMessage(null);
    onClose();
  };

  const toggle = (athleteId: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) {
        next.delete(athleteId);
      } else {
        next.add(athleteId);
      }
      return next;
    });
  };

  const toggleAll = (): void => {
    const allSelected = candidateList.every(c => selected.has(c.athleteId));
    setSelectedIds(allSelected ? new Set() : new Set(candidateList.map(c => c.athleteId)));
  };

  const handleSend = (): void => {
    const athleteIds = [...selected];
    setSendingTotal(athleteIds.length);
    setProcessed(0);
    sendWaivers.mutate(
      { athleteIds, templateId: template.documentId, onProgress: setProcessed },
      {
        onSuccess: sendResult => {
          const sent = sendResult.issued + sendResult.sentExisting;
          if (sendResult.failed.length === 0) {
            successNotification(
              `Waiver sent to ${sent} ${sent === 1 ? 'athlete' : 'athletes'}`,
              'Signed waivers appear under Documents > Issued.',
            );
            handleClose();
            return;
          }
          setResult(sendResult);
          setStep('result');
        },
        onError: error => {
          setResult(error.partialResult);
          setStoppedMessage(error.message);
          setStep('result');
        },
      },
    );
  };

  const nameFor = (athleteId: string, name: string | null): string =>
    name ?? candidateList.find(c => c.athleteId === athleteId)?.fullName ?? 'Unknown athlete';

  const sentCount = result ? result.issued + result.sentExisting : 0;
  const resultTitle = sentCount === 0 ? 'Waivers Not Sent' : "Some Waivers Weren't Sent";
  const titles = { template: 'Select Waiver Template', review: 'Review & Send', result: resultTitle };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>{titles[step]}</Title>}
      size='lg'
      centered
      fullScreen={fullScreen}
      closeOnClickOutside={!sendWaivers.isLoading}
      closeOnEscape={!sendWaivers.isLoading}
      withCloseButton={!sendWaivers.isLoading}
      data-cy='sendWaiverModal'
    >
      {step === 'template' && (
        <Stack spacing='md'>
          <DocumentAutoComplete
            label='Waiver Template'
            placeholder='Select waiver template'
            type='waiver'
            isTemplate
            value={template}
            onChange={item => setTemplate(item ?? EMPTY_TEMPLATE)}
            data-cy='sendWaiverTemplateSelect'
          />
          <Text size='xs' color='dimmed'>
            Need a different template?{' '}
            {showTemplatesLink ? (
              <Anchor component={Link} to='/documents/templates' size='xs'>
                Manage templates in Documents &gt; Templates.
              </Anchor>
            ) : (
              'Manage templates in Documents > Templates on a larger screen.'
            )}
          </Text>
          <Group position='right' spacing='sm'>
            <Button variant='subtle' color='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep('review')}
              disabled={!template.documentId}
              data-cy='sendWaiverContinueBtn'
            >
              Continue
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'review' && (
        <Stack spacing='md'>
          <KiloAlert>
            Each selected athlete will get an email with a link to review and sign {template.label}. Athletes
            who already hold an unsigned waiver are sent that one instead. Uncheck anyone who shouldn&apos;t
            get it, for example athletes who already signed in person during migration.
          </KiloAlert>

          <AthleteCandidateTable
            candidates={candidateList}
            selectedIds={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            isLoading={isLoading}
            disabled={sendWaivers.isLoading}
            checkboxDataCy='sendWaiverCandidateCheckbox'
          />

          <Text size='xs' color='dimmed'>
            Only members are selected by default.
          </Text>

          <Group position='apart'>
            <Text size='sm' data-cy='sendWaiverSelectedCount'>
              {sendWaivers.isLoading
                ? `Sending ${processed} of ${sendingTotal}...`
                : `${selected.size} of ${candidateList.length} athletes selected`}
            </Text>
            <Group spacing='sm'>
              <Button
                variant='subtle'
                color='gray'
                onClick={() => setStep('template')}
                disabled={sendWaivers.isLoading}
              >
                Back
              </Button>
              <Button
                onClick={handleSend}
                loading={sendWaivers.isLoading}
                disabled={selected.size === 0}
                data-cy='sendWaiverBtn'
              >
                Send waiver
              </Button>
            </Group>
          </Group>
        </Stack>
      )}

      {step === 'result' && result && (
        <Stack spacing='md' data-cy='sendWaiverResult'>
          {stoppedMessage && <KiloAlert variantStyle='error'>Sending stopped: {stoppedMessage}</KiloAlert>}
          <Text>
            Sent to {sentCount} of {sendingTotal} athletes.
            {result.failed.length > 0 && " These weren't sent:"}
          </Text>
          {result.failed.length > 0 && (
            <List size='sm' spacing='xs'>
              {result.failed.map(failure => (
                <List.Item key={failure.athleteId}>
                  {nameFor(failure.athleteId, failure.name)}:{' '}
                  {FAILURE_REASONS[failure.reason] ?? "Couldn't be sent, try again"}
                </List.Item>
              ))}
            </List>
          )}
          <Group position='right'>
            <Button onClick={handleClose}>Done</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default SendWaiverModal;
